import { useState, useEffect, useMemo } from 'react';
import {
  loadBetaRequests,
  loadBetaTesterInsights,
  grantBetaAccess,
  revokeBetaAccess,
  revenueCatUrl,
} from '../lib/betaStore';
import {
  mergeInsights,
  isComplete,
  isStaleGrant,
  sortGranted,
  formatAge,
  missingProfileFields,
  STALE_DAYS,
} from '../lib/betaInsights';
import PortalHeader from '../components/PortalHeader';
import PageHeader from '../components/PageHeader';

function sourceLabel(r) {
  return [r.interest && 'interest', r.inApp && 'in-app'].filter(Boolean).join(', ') || '—';
}

function UserIdCell({ userId }) {
  return (
    <a href={revenueCatUrl(userId)} target="_blank" rel="noreferrer" title="View in RevenueCat">
      {userId.slice(0, 8)}…
    </a>
  );
}

/** A sortable header cell. `active` shows the current direction arrow. */
function SortHeader({ label, sortKey, sort, onSort, title }) {
  const active = sort.key === sortKey;
  return (
    <th
      className={`beta-sortable${active ? ' beta-sorted' : ''}`}
      onClick={() => onSort(sortKey)}
      title={title || `Sort by ${label.toLowerCase()}`}
    >
      {label}
      <span className="beta-sort-arrow">{active ? (sort.direction === 'asc' ? '▲' : '▼') : ''}</span>
    </th>
  );
}

/**
 * Engagement cells share one rule: a value that was not measured renders "?",
 * never "0" or "never". A zero is a measurement; a missing insight — or a
 * signal that failed inside an otherwise-loaded one — is not, and conflating
 * them would invite a revoke based on a failed read.
 */
function Unknown({ reason }) {
  return <span className="beta-unknown" title={reason || 'Insights unavailable'}>?</span>;
}

/** A measured number, or "?" when that particular signal did not resolve. */
function Measured({ insight, value, signal }) {
  if (!insight) return <Unknown />;
  if (value === null || value === undefined) {
    return <Unknown reason={`The ${signal} lookup failed for this user — not a measured zero`} />;
  }
  return value;
}

export default function BetaRequests() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState({});

  // Engagement/profile insights come from the admin-gated callable and load
  // AFTER the table — the grant list must render even if the callable is
  // unavailable, because revoking is the page's core job.
  const [insights, setInsights] = useState(new Map());
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState(null);

  const [sort, setSort] = useState({ key: 'lastActive', direction: 'asc' });
  const [staleOnly, setStaleOnly] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const loaded = await loadBetaRequests();
      setRows(loaded);
      await loadInsights(loaded);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadInsights(loadedRows) {
    const uids = loadedRows.filter((r) => r.granted).map((r) => r.user_id);
    if (uids.length === 0) {
      setInsights(new Map());
      return;
    }
    setInsightsLoading(true);
    setInsightsError(null);
    try {
      setInsights(await loadBetaTesterInsights(uids));
    } catch (e) {
      setInsights(new Map());
      setInsightsError(e.message);
    } finally {
      setInsightsLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleGrant(userId) {
    setBusy((b) => ({ ...b, [userId]: true }));
    try {
      await grantBetaAccess(userId);
      await load();
    } finally {
      setBusy((b) => ({ ...b, [userId]: false }));
    }
  }

  async function handleRevoke(userId) {
    if (!confirm(`Revoke beta access for ${userId}?`)) return;
    setBusy((b) => ({ ...b, [userId]: true }));
    try {
      await revokeBetaAccess(userId);
      await load();
    } finally {
      setBusy((b) => ({ ...b, [userId]: false }));
    }
  }

  function handleSort(key) {
    setSort((s) => (
      s.key === key
        ? { key, direction: s.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: key === 'email' ? 'asc' : 'asc' }
    ));
  }

  const pending = useMemo(() => rows.filter((r) => !r.granted), [rows]);

  // The filter only APPLIES while insights are available. Disabling the
  // checkbox is not enough: an operator who ticked it before a reload whose
  // insight fetch then failed would keep a filter that now matches nothing —
  // every row unknown, so every row excluded — and the whole revoke list, the
  // point of the page, would silently vanish behind a banner saying the filter
  // was off. The banner and the table have to agree.
  const staleFilterActive = staleOnly && !insightsError;

  const granted = useMemo(() => {
    const withInsights = mergeInsights(rows.filter((r) => r.granted), insights);
    const filtered = staleFilterActive
      ? withInsights.filter((r) => isStaleGrant(r))
      : withInsights;
    return sortGranted(filtered, sort.key, sort.direction);
  }, [rows, insights, staleFilterActive, sort]);

  // Two counts, because they answer different questions: how many grants are
  // measurably stale, and how many could not be measured at all. The second is
  // what stops "0 stale" from being read as "everyone is active".
  const { staleCount, incompleteCount, measuredCount } = useMemo(() => {
    const all = mergeInsights(rows.filter((r) => r.granted), insights);
    const incomplete = all.filter((r) => !isComplete(r.insight)).length;
    return {
      staleCount: all.filter((r) => isStaleGrant(r)).length,
      incompleteCount: incomplete,
      measuredCount: all.length - incomplete,
    };
  }, [rows, insights]);

  if (loading) return <div className="page"><p>Loading beta requests…</p></div>;
  if (error) return <div className="page"><p className="error">Error: {error}</p></div>;

  const grantedTotal = rows.filter((r) => r.granted).length;

  return (
    <>
      <PortalHeader />
      <div className="page beta-page">
        <PageHeader backTo="/admin" backLabel="Admin" title="Beta Requests" />

      <section>
        <h2>Pending ({pending.length})</h2>
        {pending.length === 0 ? (
          <p>No pending requests.</p>
        ) : (
          <div className="beta-table-wrap">
          <table className="beta-table">
            <thead>
              <tr>
                <th>User ID</th>
                <th>Email</th>
                <th>Club</th>
                <th>Platform</th>
                <th>First seen</th>
                <th>Source</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pending.map((r) => (
                <tr key={r.user_id}>
                  <td><UserIdCell userId={r.user_id} /></td>
                  <td>{r.email || '—'}</td>
                  <td>{r.club || '—'}</td>
                  <td>{r.platform || '—'}</td>
                  <td>{r.earliest ? r.earliest.toLocaleDateString() : '—'}</td>
                  <td>{sourceLabel(r)}</td>
                  <td>
                    <button
                      onClick={() => handleGrant(r.user_id)}
                      disabled={busy[r.user_id]}
                    >
                      {busy[r.user_id] ? 'Granting…' : 'Grant'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </section>

      <section>
        <h2>
          Granted ({staleFilterActive ? `${granted.length} of ${grantedTotal}` : grantedTotal})
        </h2>

        {grantedTotal > 0 && (
          <div className="beta-toolbar">
            <label className="beta-filter">
              <input
                type="checkbox"
                checked={staleOnly}
                onChange={(e) => setStaleOnly(e.target.checked)}
                disabled={insightsLoading || Boolean(insightsError)}
              />
              {' '}Stale only — no activity in {STALE_DAYS} d, or an empty profile
              {!insightsLoading && !insightsError && ` (${staleCount})`}
            </label>
            {!insightsLoading && !insightsError && incompleteCount > 0 && (
              <span className="beta-note beta-note-warn">
                {incompleteCount} grant{incompleteCount === 1 ? '' : 's'} could not be
                measured (a server-side lookup failed) — shown with “?” and excluded
                from the stale filter, not counted as active.
              </span>
            )}
            {insightsLoading && <span className="beta-note">Loading engagement…</span>}
            {insightsError && (
              <span className="beta-note beta-note-warn">
                Engagement unavailable ({insightsError}) — sign-in, races, reports and
                profile show “?”, and the stale filter is off. Revoke on the other
                columns only.
              </span>
            )}
          </div>
        )}

        {grantedTotal === 0 ? (
          <p>No active beta testers.</p>
        ) : granted.length === 0 ? (
          <p>
            {measuredCount === 0
              ? `No grant could be measured — ${incompleteCount} row(s) are missing server-side data, so none can be called stale.`
              : `No stale grants among the ${measuredCount} measured — each has been active in the last ${STALE_DAYS} days.`}
            {measuredCount > 0 && incompleteCount > 0
              && ` ${incompleteCount} further row(s) could not be measured.`}
          </p>
        ) : (
          <div className="beta-table-wrap">
          <table className="beta-table">
            <thead>
              <tr>
                <th>User ID</th>
                <SortHeader label="Email" sortKey="email" sort={sort} onSort={handleSort} />
                <th>Club</th>
                <th>Platform</th>
                <SortHeader
                  label="First seen"
                  sortKey="firstSeen"
                  sort={sort}
                  onSort={handleSort}
                />
                <th>Source</th>
                <th>Tier</th>
                <SortHeader
                  label="Last active"
                  sortKey="lastActive"
                  sort={sort}
                  onSort={handleSort}
                  title="Newest of sign-in, token refresh, device check-in and last race"
                />
                <SortHeader
                  label="Races"
                  sortKey="races"
                  sort={sort}
                  onSort={handleSort}
                  title="Races recorded, and when the last one was"
                />
                <th title="Analysis reports linked to this user">Reports</th>
                <SortHeader
                  label="Profile"
                  sortKey="profile"
                  sort={sort}
                  onSort={handleSort}
                  title="Core profile fields set: name, club, boat, polars"
                />
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {granted.map((r) => {
                const ins = r.insight;
                const stale = isStaleGrant(r);
                const missing = missingProfileFields(ins);
                return (
                  <tr key={r.user_id} className={stale ? 'beta-row-stale' : undefined}>
                    <td><UserIdCell userId={r.user_id} /></td>
                    <td>
                      {r.email || (ins && ins.email) || '—'}
                      {ins && ins.authExists === false && (
                        <span className="beta-badge beta-badge-warn" title="No auth account for this uid">
                          deleted
                        </span>
                      )}
                      {ins && ins.disabled && (
                        <span className="beta-badge beta-badge-warn" title="Auth account disabled">
                          disabled
                        </span>
                      )}
                    </td>
                    <td>{r.club || (ins && ins.clubId) || '—'}</td>
                    <td>
                      {r.platform || (ins && (ins.platforms || []).join(', ')) || '—'}
                    </td>
                    <td>{r.earliest ? r.earliest.toLocaleDateString() : '—'}</td>
                    <td>{sourceLabel(r)}</td>
                    <td>{r.tier}</td>
                    <td
                      className="beta-num"
                      title={ins && ins.lastActiveAt ? new Date(ins.lastActiveAt).toLocaleString() : undefined}
                    >
                      {!ins ? <Unknown />
                        : !isComplete(ins) ? <Unknown reason={`Incomplete: ${(ins.signalErrors || []).join(', ')} lookup failed — an incomplete clock can only look older than it is`} />
                          : ins.lastActiveAt ? formatAge(ins.lastActiveAt) : 'never'}
                    </td>
                    <td
                      className="beta-num"
                      title={ins && ins.lastRaceAt ? `Last race ${new Date(ins.lastRaceAt).toLocaleDateString()}` : undefined}
                    >
                      <Measured insight={ins} value={ins && ins.raceCount} signal="race history" />
                    </td>
                    <td className="beta-num">
                      <Measured insight={ins} value={ins && ins.reportCount} signal="analysis report" />
                    </td>
                    <td
                      className="beta-num"
                      title={missing ? `Missing: ${missing}` : undefined}
                    >
                      {!ins ? <Unknown />
                        : !ins.profile ? <Unknown reason="A profile lookup failed for this user — not an empty profile" />
                          : `${ins.profile.setCount}/${ins.profile.total}`}
                    </td>
                    <td>
                      <button
                        onClick={() => handleRevoke(r.user_id)}
                        disabled={busy[r.user_id]}
                        className="danger"
                      >
                        {busy[r.user_id] ? 'Revoking…' : 'Revoke'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </section>
      </div>
    </>
  );
}
